<script lang="ts" setup>
import type { FocusEvent } from '@/objects/generated/classes/FocusEvent';
import { onActivated, ref } from 'vue';
import { EventDao } from '@/dao';
import { useCommonStore, useToastStore } from '@/stores';
import { useIsViewActive, useTaskLoader } from '@/composeables/loaders';
import FocusEventList from '@/components/focuseventcard/FocusEventList.vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { LoadingComp } from '@/components';

const focusevents = ref<FocusEvent[]>([]);
const loaded = ref(false);

const { AddLoader, LoadState } = useTaskLoader();

const isViewActive = useIsViewActive();

const loadFocusEvents = async () => {
    try {
        focusevents.value = await EventDao.Focus();
        loaded.value = true;
    } catch (error: any) {
        useToastStore().add({ level: 'ERROR', message: error.message });
        throw error;
    }
};

AddLoader(loadFocusEvents, 'eventinfo.load');

const commonStore = useCommonStore();

AddLoader(async () => {
    await commonStore.EnsureLoaded(['Organizers']);
    await subscribeSignalrIfNotAlreadySubscribed();
}, '');

let reloadNeeded = false;

onActivated(() => {
    if (reloadNeeded) {
        loadFocusEvents();
        reloadNeeded = false;
    }
    subscribeSignalrIfNotAlreadySubscribed();
});

let signalrUnsubscribeFunctions: Array<() => Promise<void>> = [];
let signalrSubscribePromises: Array<Promise<() => Promise<void>>> = [];
const subscribeSignalrIfNotAlreadySubscribed = async () => {
    // we are already subscribing
    if (signalrSubscribePromises.length) {
        return;
    }
    commonStore.Organizers.map((o) => o.ID).forEach((organizerId) => {
        console.log('Subscribing eventchanged for organizer', organizerId);
        signalrSubscribePromises.push(
            signalrSubscribe.DbDataChangedNotification({ OrganizerID: organizerId, DtoType: 'FocusEvent' }, async (notifications) => {
                // Reload if we add
                if (notifications.some((n) => n.Operation == 'Insert')) {
                    reloadNeeded = true;
                }

                // Reload if we change one thats in the list
                const ids = notifications.filter((n) => ['Update', 'Delete'].includes(n.Operation)).map((n) => n.ID);
                if (focusevents.value.map((e) => e.ID).some((id) => ids.includes(id))) {
                    reloadNeeded = true;
                }

                if (reloadNeeded && isViewActive.value) {
                    await loadFocusEvents();
                    reloadNeeded = false;
                } else {
                    // If the view is not active, we can unsubsribe now
                    signalrUnsubscribeFunctions.forEach(async (unsub) => await unsub());
                    signalrUnsubscribeFunctions = [];
                }
            }),
        );
    });

    signalrUnsubscribeFunctions.push(...(await Promise.all(signalrSubscribePromises)));
};
</script>

<template>
    <div class="d-flex flex-column w-100 gap-3">
        <LoadingComp
            :label="$tm('focus.loading_events')"
            :state="LoadState"
        />
        <template v-if="focusevents.length != 0">
            <h5>{{ $tm('home.focus') }}</h5>
            <FocusEventList :events="focusevents" />
        </template>
    </div>
</template>
